import { Theme, alpha } from "@mui/material/styles";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

export const getLoginStyles = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    root: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative" as const,
      overflow: "hidden",
      backgroundColor: isDark
        ? theme.palette.background.default
        : alpha("#eef0f5", 1),
      px: { xs: 2, sm: 3 },
      pt: 12,
      pb: 6,
    },
    backgroundLayer: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: "hidden",
      pointerEvents: "none" as const,
      zIndex: 0,
    },
    gradientOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isDark
        ? `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(
            BRAND,
            0.22
          )} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 100% 100%, ${alpha(
            BRAND,
            0.14
          )} 0%, transparent 50%)`
        : `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(
            BRAND,
            0.14
          )} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 100% 100%, ${alpha(
            BRAND,
            0.1
          )} 0%, transparent 50%)`,
      zIndex: 0,
    },
    orb1: {
      position: "absolute" as const,
      top: "8%",
      right: "12%",
      width: 380,
      height: 380,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${alpha(
        BRAND,
        isDark ? 0.28 : 0.16
      )} 0%, transparent 65%)`,
      filter: "blur(55px)",
      animation: "authPulse 8s ease-in-out infinite",
    },
    orb2: {
      position: "absolute" as const,
      bottom: "12%",
      left: "8%",
      width: 320,
      height: 320,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${alpha(
        BRAND,
        isDark ? 0.22 : 0.12
      )} 0%, transparent 65%)`,
      filter: "blur(50px)",
      animation: "authPulse 9s ease-in-out infinite 2s",
    },
    orb3: {
      position: "absolute" as const,
      top: "55%",
      left: "50%",
      width: 260,
      height: 260,
      borderRadius: "50%",
      background: `radial-gradient(circle, ${alpha(
        theme.palette.divider,
        isDark ? 0.12 : 0.1
      )} 0%, transparent 70%)`,
      filter: "blur(45px)",
      transform: "translate(-50%, -50%)",
    },
    gridOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `linear-gradient(${alpha(
        theme.palette.divider,
        isDark ? 0.12 : 0.14
      )} 1px, transparent 1px), linear-gradient(90deg, ${alpha(
        theme.palette.divider,
        isDark ? 0.12 : 0.14
      )} 1px, transparent 1px)`,
      backgroundSize: "48px 48px",
      opacity: 1,
    },
    floatShape1: {
      position: "absolute" as const,
      top: "20%",
      left: "15%",
      width: 80,
      height: 80,
      borderRadius: "20%",
      border: `2px solid ${alpha(BRAND, isDark ? 0.25 : 0.18)}`,
      animation: "authFloat 12s ease-in-out infinite",
    },
    floatShape2: {
      position: "absolute" as const,
      bottom: "25%",
      right: "18%",
      width: 60,
      height: 60,
      borderRadius: "50%",
      background: alpha(BRAND, isDark ? 0.1 : 0.08),
      animation: "authFloat 10s ease-in-out infinite 1s",
    },
    cardWrapper: {
      position: "relative" as const,
      zIndex: 1,
      width: "100%",
      maxWidth: 440,
    },
    card: {
      borderRadius: "24px",
      overflow: "hidden" as const,
      background: theme.palette.background.paper,
      border: `1px solid ${
        isDark
          ? alpha(theme.palette.divider, 0.2)
          : alpha(theme.palette.divider, 0.25)
      }`,
      boxShadow: isDark
        ? `0 24px 64px ${alpha(
            theme.palette.common.black,
            0.4
          )}, 0 0 0 1px ${alpha(theme.palette.divider, 0.1)}`
        : `0 32px 64px ${alpha(
            theme.palette.common.black,
            0.08
          )}, 0 0 0 1px ${alpha(theme.palette.divider, 0.06)}`,
      position: "relative" as const,
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: 5,
        height: "100%",
        background: `linear-gradient(180deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
        borderRadius: "5px 0 0 5px",
      },
    },
    cardInner: {
      p: { xs: 3, sm: 4 },
      pl: { xs: 4, sm: 5 },
    },
    logoRow: {
      display: "flex",
      alignItems: "center",
      mb: 2,
    },
    welcomeBlock: {
      mb: 3,
    },
    form: {
      mt: 0,
    },
    actionsRow: {
      display: "flex",
      justifyContent: "flex-end",
      mb: 2,
    },
    signUpRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap" as const,
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderWidth: 1.5,
          borderColor: isDark
            ? alpha(theme.palette.divider, 0.5)
            : alpha(theme.palette.common.black, 0.22),
        },
        "&:hover fieldset": {
          borderColor: alpha(BRAND, 0.6),
        },
        "&.Mui-focused fieldset": {
          borderColor: BRAND,
          borderWidth: 2,
          boxShadow: `0 0 0 4px ${alpha(BRAND, 0.15)}`,
        },
      },
    },
    primaryButton: {
      minHeight: 48,
      py: 1.5,
      fontSize: "1rem",
    },
    outlineButton: {
      minHeight: 48,
      py: 1.5,
      fontSize: "1rem",
    },
  };
};

const loginStyles = {
  root: {},
  cardWrapper: {},
  card: {},
  cardInner: {},
  logoRow: {},
  form: {},
  actionsRow: {},
  signUpRow: {},
};

export default loginStyles;
